import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Users, Coffee, TrendingUp, Zap, Power, LogOut, Settings } from "lucide-react";
import { toast } from "sonner";
import { superLogin, superApi, getSuperPin, clearSuperPin } from "../lib/admin";
import CustomerBoost from "./CustomerBoost";
import GrowthAnalytics from "./GrowthAnalytics";
import TenantOps from "../components/TenantOps";
import OpsAlerts from "../components/OpsAlerts";

const SuperAdmin = () => {
    const nav = useNavigate();
    const [pin, setPin] = useState("");
    const [authed, setAuthed] = useState(!!getSuperPin());
    const [tenants, setTenants] = useState([]);
    const [stats, setStats] = useState(null);
    const [network, setNetwork] = useState(null);
    const [loading, setLoading] = useState(false);
    const [financials, setFinancials] = useState(null);
    const [opsTenant, setOpsTenant] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [t, s, n, f] = await Promise.all([
                superApi.get("/super/tenants"),
                superApi.get("/super/stats"),
                superApi.get("/super/network"),
                superApi.get("/super/financials?period=30d"),
            ]);
            setTenants(t.data.tenants);
            setStats(s.data);
            setNetwork(n.data);
            setFinancials(f.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearSuperPin();
                setAuthed(false);
                toast.error("Session expired. Log in again.");
            } else {
                toast.error("Failed to load platform data");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authed) load();
    }, [authed]);

    const doLogin = async () => {
        if (!pin) return;
        try {
            await superLogin(pin);
            setAuthed(true);
            setPin("");
        } catch {
            toast.error("Wrong PIN");
        }
    };

    const toggleStatus = async (t) => {
        const next = t.status === "active" ? "suspended" : "active";
        try {
            await superApi.patch(`/super/tenants/${t.slug}`, { status: next });
            toast.success(`${t.name} · ${next}`);
            load();
        } catch {
            toast.error("Update failed");
        }
    };

    const changePlan = async (t, plan) => {
        try {
            await superApi.patch(`/super/tenants/${t.slug}`, { plan });
            toast.success(`${t.name} → ${plan}`);
            load();
        } catch {
            toast.error("Update failed");
        }
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex flex-col items-center justify-center p-6">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <button
                        data-testid="super-back-btn"
                        onClick={() => nav("/")}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12] mb-8"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back
                    </button>
                    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-8">
                        <div className="mx-auto h-14 w-14 rounded-2xl bg-[#2C1A12] flex items-center justify-center">
                            <Shield className="h-6 w-6 text-[#FF4F18]" strokeWidth={2.5} />
                        </div>
                        <div className="mt-6 text-center">
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                Coffeein7 Platform
                            </div>
                            <h1 className="mt-2 font-display font-black text-3xl">Super-admin</h1>
                            <p className="mt-2 text-[#8C7A72] text-sm">
                                Enter your master password to manage all cafés.
                            </p>
                        </div>
                        <input
                            data-testid="super-pin-input"
                            type="password"
                            autoComplete="current-password"
                            placeholder="Master password"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && doLogin()}
                            className="mt-6 w-full px-4 py-4 bg-[#F9F6F0] border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] font-mono text-base tracking-wide text-center"
                        />
                        <button
                            data-testid="super-login-btn"
                            onClick={doLogin}
                            disabled={!pin}
                            className="mt-4 w-full py-4 rounded-full bg-[#FF4F18] text-white font-display font-bold disabled:opacity-50 active:scale-95"
                        >
                            Enter dashboard
                        </button>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <header className="border-b border-[#E8DCCB]/60 bg-white/80 backdrop-blur sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-[#2C1A12] flex items-center justify-center">
                            <Shield className="h-4 w-4 text-[#FF4F18]" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8C7A72]">
                                Coffeein7 Platform
                            </div>
                            <div className="font-display font-black text-lg leading-none">
                                Super-admin
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                    <a href="/Coffeein7-Cafe-Partner-Deck.pptx" download data-testid="super-partner-deck-link" className="text-sm font-semibold text-[#8C7A72] hover:text-[#FF4F18]">
                        Partner deck (.pptx)
                    </a>
                    <a href="/pitch" data-testid="super-pitch-link" className="text-sm font-semibold text-[#8C7A72] hover:text-[#FF4F18]">
                        Pitch deck
                    </a>
                    <button
                        data-testid="super-logout"
                        onClick={() => {
                            clearSuperPin();
                            setAuthed(false);
                        }}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#FF4F18]"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={2.5} /> Log out
                    </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-10">
                {/* Platform stats */}
                <section>
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        Platform
                    </div>
                    <h2 className="mt-2 font-display font-black text-3xl">The whole network.</h2>

                    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Stat
                            icon={Coffee}
                            label="Cafés"
                            value={stats?.total_tenants ?? "—"}
                            sub={stats ? `${stats.active_tenants} active` : ""}
                            testid="stat-tenants"
                        />
                        <Stat
                            icon={Users}
                            label="Consumers"
                            value={network?.total_consumers ?? "—"}
                            sub={network ? `${network.avg_cafes_per_consumer} cafés avg` : ""}
                            testid="stat-consumers"
                        />
                        <Stat
                            icon={Zap}
                            label="Power users"
                            value={network?.power_users ?? "—"}
                            sub={network ? `${network.power_user_pct}% of consumers · 2+ cafés` : ""}
                            testid="stat-power-users"
                            highlight
                        />
                        <Stat
                            icon={TrendingUp}
                            label="Platform GMV"
                            value={stats ? `$${stats.total_gmv.toLocaleString()}` : "—"}
                            sub={stats ? `${stats.orders_7d} orders in 7d` : ""}
                            testid="stat-gmv"
                        />
                    </div>
                </section>

                {/* Coffeein7 revenue — subscription + transaction, ALWAYS separated */}
                {financials && (
                    <section className="mt-14" data-testid="platform-revenue">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            Coffeein7 revenue · {financials.period.label}
                        </div>
                        <h2 className="mt-2 font-display font-black text-3xl">Two streams. Never mixed.</h2>

                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                            {/* A) Subscription revenue */}
                            <div className="rounded-3xl bg-white border border-[#E8DCCB] p-6" data-testid="rev-subs">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                    A · Café subscriptions
                                </div>
                                <div className="mt-2 font-display font-black text-3xl">
                                    ${(financials.subscription_revenue.mrr_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-[#8C7A72] mt-1">
                                    MRR · {financials.subscription_revenue.active_paying_cafes} paying cafés
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                    <div className="rounded-xl bg-[#F9F6F0] p-3 border border-[#E8DCCB]">
                                        <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Starter · $49</div>
                                        <div className="mt-1 font-display font-black text-lg">
                                            {financials.subscription_revenue.cafes_by_plan.starter || 0}
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-[#F9F6F0] p-3 border border-[#E8DCCB]">
                                        <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Pro · $79</div>
                                        <div className="mt-1 font-display font-black text-lg">
                                            {financials.subscription_revenue.cafes_by_plan.pro || 0}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 text-[11px] text-[#8C7A72]">
                                    ARR ${(financials.subscription_revenue.arr_cents / 100).toLocaleString()}
                                </div>
                            </div>

                            {/* B) Transaction fee revenue */}
                            <div className="rounded-3xl bg-[#2C1A12] text-white p-6 relative overflow-hidden" data-testid="rev-txn">
                                <div className="absolute -right-8 -bottom-8 h-40 w-40 rounded-full bg-[#FF4F18]/30" />
                                <div className="relative">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4F18] font-bold">
                                        B · Transaction fees
                                    </div>
                                    <div className="mt-2 font-display font-black text-3xl">
                                        ${(financials.transaction_revenue.coffeein7_fee_net_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-xs text-white/60 mt-1">
                                        net of reversals · {financials.transaction_revenue.orders} paid orders
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                        <div className="rounded-xl bg-white/5 p-3 border border-white/10">
                                            <div className="text-[10px] uppercase tracking-wider text-white/50">Starter · 7%</div>
                                            <div className="mt-1 font-display font-black text-lg">
                                                ${((financials.transaction_revenue.by_plan?.starter?.fee_cents || 0) / 100).toFixed(2)}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-white/5 p-3 border border-white/10">
                                            <div className="text-[10px] uppercase tracking-wider text-white/50">Pro · 5%</div>
                                            <div className="mt-1 font-display font-black text-lg">
                                                ${((financials.transaction_revenue.by_plan?.pro?.fee_cents || 0) / 100).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-[11px] text-white/50">
                                        On ${(financials.transaction_revenue.gross_customer_sales_cents / 100).toLocaleString()} gross café sales
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-4 text-sm text-[#8C7A72]">
                            <span className="font-display font-black text-[#2C1A12]">Coffeein7 gross revenue:</span>{" "}
                            <span
                                className="font-display font-black text-[#FF4F18] text-lg"
                                data-testid="platform-gross-revenue"
                            >
                                ${(financials.totals.coffeein7_gross_revenue_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>{" "}
                            = MRR + transaction fees (net). Gross café sales are NOT counted as our revenue —
                            that's pass-through to cafés.
                        </div>
                    </section>
                )}

                {/* Growth analytics — country, deltas, sparklines */}
                <GrowthAnalytics />

                {/* Customer boost — search + reward + audit */}
                <CustomerBoost />

                <OpsAlerts />

                {/* Tenants list */}
                <section className="mt-14">
                    <div className="flex items-baseline justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                All cafés
                            </div>
                            <h2 className="mt-2 font-display font-black text-3xl">
                                {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
                            </h2>
                        </div>
                    </div>

                    <div className="mt-6 grid gap-3">
                        {loading && <div className="text-center py-8 text-[#8C7A72]">Loading…</div>}
                        {!loading && tenants.length === 0 && (
                            <div className="text-center py-8 text-[#8C7A72]">No cafés yet.</div>
                        )}
                        {tenants.map((t) => (
                            <div
                                key={t.slug}
                                className={`rounded-3xl bg-white border p-5 grid md:grid-cols-[1fr_auto_auto_auto] items-center gap-4 ${
                                    t.status === "suspended" ? "border-[#FF4F18]/40 opacity-70" : "border-[#E8DCCB]"
                                }`}
                                data-testid={`super-tenant-${t.slug}`}
                            >
                                <div>
                                    <div className="flex items-baseline gap-3">
                                        <div className="font-display font-black text-xl">{t.name}</div>
                                        <div className="text-xs text-[#8C7A72] font-mono">
                                            /{t.slug}
                                        </div>
                                        {t.is_founding_tenant && (
                                            <span className="text-[9px] uppercase tracking-[0.2em] bg-[#FF4F18] text-white font-black px-2 py-0.5 rounded-full">
                                                Founding
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-xs text-[#8C7A72]">
                                        {t.owner_email || "no email"} · {t.owner_phone ? `···${t.owner_phone.slice(-4)}` : "no phone"}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-4 text-xs">
                                        <span>
                                            <span className="text-[#8C7A72]">Orders:</span>{" "}
                                            <span className="font-bold">{t.stats?.orders ?? 0}</span>
                                        </span>
                                        <span>
                                            <span className="text-[#8C7A72]">GMV:</span>{" "}
                                            <span className="font-bold">${(t.stats?.revenue ?? 0).toFixed(2)}</span>
                                        </span>
                                        <span>
                                            <span className="text-[#8C7A72]">Customers:</span>{" "}
                                            <span className="font-bold">{t.stats?.unique_customers ?? 0}</span>
                                        </span>
                                    </div>
                                </div>

                                <select
                                    data-testid={`super-plan-${t.slug}`}
                                    value={t.plan || "starter"}
                                    onChange={(e) => changePlan(t, e.target.value)}
                                    className="rounded-full bg-[#F9F6F0] border border-[#E8DCCB] px-4 py-2 text-sm font-semibold outline-none focus:border-[#FF4F18]"
                                >
                                    <option value="starter">Starter</option>
                                    <option value="pro">Pro</option>
                                    <option value="multi">Multi</option>
                                </select>

                                <button
                                    data-testid={`super-toggle-${t.slug}`}
                                    onClick={() => toggleStatus(t)}
                                    className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold border ${
                                        t.status === "active"
                                            ? "bg-[#1B995E]/10 text-[#1B995E] border-[#1B995E]/30"
                                            : "bg-[#FF4F18]/10 text-[#FF4F18] border-[#FF4F18]/30"
                                    }`}
                                >
                                    <Power className="h-3 w-3" strokeWidth={3} />
                                    {t.status === "active" ? "Active" : "Suspended"}
                                </button>

                                <button
                                    data-testid={`super-ops-${t.slug}`}
                                    onClick={() => setOpsTenant(t)}
                                    className="rounded-full bg-white border-2 border-[#2C1A12] text-[#2C1A12] px-4 py-2 text-xs font-display font-bold active:scale-95 flex items-center gap-1"
                                >
                                    <Settings className="h-3 w-3" strokeWidth={3} />
                                    Ops
                                </button>

                                <button
                                    data-testid={`super-open-${t.slug}`}
                                    onClick={() => nav(`/${t.slug}`)}
                                    className="rounded-full bg-[#2C1A12] text-white px-4 py-2 text-xs font-display font-bold active:scale-95"
                                >
                                    Open
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* Tenant operations drawer */}
            {opsTenant && (
                <TenantOps
                    tenant={opsTenant}
                    onClose={() => setOpsTenant(null)}
                    onChanged={load}
                />
            )}
        </div>
    );
};

const Stat = ({ icon: Icon, label, value, sub, highlight, testid }) => (
    <div
        className={`rounded-3xl p-5 border ${
            highlight
                ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                : "bg-white text-[#2C1A12] border-[#E8DCCB]"
        }`}
        data-testid={testid}
    >
        <div className="flex items-center gap-2">
            <Icon
                className={`h-4 w-4 ${highlight ? "text-[#FF4F18]" : "text-[#8C7A72]"}`}
                strokeWidth={2.5}
            />
            <div
                className={`text-[10px] uppercase tracking-[0.25em] font-bold ${
                    highlight ? "text-white/70" : "text-[#8C7A72]"
                }`}
            >
                {label}
            </div>
        </div>
        <div className="mt-3 font-display font-black text-3xl">{value}</div>
        {sub && (
            <div className={`mt-1 text-[11px] ${highlight ? "text-white/60" : "text-[#8C7A72]"}`}>
                {sub}
            </div>
        )}
    </div>
);

export default SuperAdmin;
